import { useMemo } from "react";
import { useSelector } from "react-redux";

import BackgroundImage from "../../shared/image/placeholder.jpg";
import InsightsImageCard from "../pricing-reports/design/ImageCard";
import {
  formatImageCardRetailerProvinceDescription,
  formatInsightsImageCardAriaLabel,
} from "../pricing-reports/pricingReportCardDisplay";
import {
  PRICING_REPORT_AGGREGATE_CARD_IMAGE_TRANSFORM_KEY,
  withDirectusAssetTransformKey,
} from "../pricing-reports/pricingReportImageUrls";
import { formatAnomalyChips } from "./verifyPriceAnomalies";
import VerifyPriceCardActions from "./VerifyPriceCardActions";

function buildVerifyPricePrimaryMetric(item) {
  if (!item?.unitPrice || item.unitPrice === "—") return null;
  const compact = String(item.unitPrice).replace(" per ", " / ");
  return { label: "Unit price", value: compact };
}

function buildVerifyPriceMetaChips(item) {
  const chips = [];
  if (item?.recordAt && item.recordAt !== "—") {
    chips.push({ label: "Date", value: String(item.recordAt) });
  }
  chips.push({
    value: item?.voided ? "Voided" : item?.verified ? "Verified" : "Not verified",
  });
  return chips;
}

export default function VerifyPriceCard({
  item,
  actionLoading,
  onCardClick,
  onAccept,
  onReject,
  onRestore,
}) {
  const retailer = useSelector((state) => state.retailer);
  const coverUrl = item?.images?.[0] || "";
  const cardCoverImageUrl = withDirectusAssetTransformKey(
    coverUrl,
    PRICING_REPORT_AGGREGATE_CARD_IMAGE_TRANSFORM_KEY
  );
  const placeholderImageUrl = retailer?.default_image
    ? `${process.env.REACT_APP_API_URL}/assets/${retailer.default_image}`
    : BackgroundImage;

  const primaryMetric = useMemo(() => buildVerifyPricePrimaryMetric(item), [item]);
  const metaChips = useMemo(
    () => [...buildVerifyPriceMetaChips(item), ...formatAnomalyChips(item?.anomaly)],
    [item]
  );
  const productTitle = item?.product || "—";
  const cardDescription = useMemo(
    () => formatImageCardRetailerProvinceDescription(item?.retailer, item?.province),
    [item?.retailer, item?.province]
  );
  const cardAriaLabel = formatInsightsImageCardAriaLabel(
    productTitle,
    cardDescription,
    primaryMetric,
    metaChips
  );

  return (
    <InsightsImageCard
      title={productTitle}
      description={cardDescription}
      imageUrl={cardCoverImageUrl}
      placeholderImageUrl={placeholderImageUrl}
      imageFit="cover"
      primaryMetric={primaryMetric}
      primaryMetricPlacement="text"
      metaChips={metaChips}
      onActivate={() => onCardClick(item)}
      activateAriaLabel={cardAriaLabel || "Open price record"}
      footer={
        <VerifyPriceCardActions
          disabled={actionLoading || !item?.id}
          voided={Boolean(item?.voided)}
          onAccept={() => onAccept(item.id)}
          onReject={() => onReject(item.id)}
          onRestore={() => onRestore(item.id)}
        />
      }
    />
  );
}
